
import React, { Component, useContext, useRef } from 'react';
import { observable, runInAction } from 'mobx';
import { FORM_ERROR, FormApi, FieldState, FieldSubscription, fieldSubscriptionItems, FormSubscription, formSubscriptionItems, FormState } from 'final-form';
import { Field, FieldRenderProps, useFormState, useField, useForm, FieldProps } from 'react-final-form';
import { AxiosError } from 'axios';
import pluralize from 'pluralize';

import { errorsToList, FieldComponentProps, FFAdapterComponent } from './FormAdapters';
import { DeclAlert } from '../AlertManager';
import { isHttpError } from '@/api';
import { captureException } from '@sentry/browser';
import { LowMask } from '../LoadingMask';
import { flatten } from '@/util/util';

type BackendErrorHash = { [k: string]: string | string[] | BackendErrorHash, base?: string | string[] }

type ToArrayType<D> = D extends any[] ? D : D[];
const toArray = <D extends any>(data: D): ToArrayType<D> => {
    if (Array.isArray(data)) return data as any;
    if (data == null) return [] as any;
    return [data] as any;
}

const upcaseFirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export function transformHttpErrors(err: AxiosError, form?: FormApi) {
    const errs: BackendErrorHash = err.response.data['errors'];

    const fieldKeysSet = form ? new Set(form.getRegisteredFields()) : null;
    const determineLocalName = (name: string) => {
        if (!fieldKeysSet || fieldKeysSet.has(name)) return name;

        let variants: string[] = [];
        const singularForm = pluralize(name, 1);
        if (singularForm.endsWith('_id')) {
            variants.push(singularForm.slice(0, -3))
        } else {
            variants.push(singularForm + '_id')
        }

        if (singularForm != name) variants = variants.map(v => pluralize(v));

        for (let v of variants) {
            if (fieldKeysSet.has(v)) return v;
        }

        return name;
    }

    const transformMessage = (message: string) => {
        return upcaseFirst(message).replace(/\b\d{5,}(\.\d+)?\b/g, (v) => parseInt(v).toLocaleString());
    }

    const transformStructure = (err_struct: BackendErrorHash, context = []) => {
        const transformedErrors = {};
        for (let [fld, ferrs] of Object.entries(err_struct)) {
            if (fld == 'base') {
                transformedErrors[FORM_ERROR] = toArray(errs[fld]).map((e) => {
                    return transformMessage(e)
                });
            } else {
                fld = determineLocalName(fld);
                if (typeof ferrs == 'string' || Array.isArray(ferrs)) {
                    transformedErrors[fld] = toArray(ferrs).map((e) => {
                        return transformMessage(e)
                    });
                } else {
                    transformedErrors[fld] = transformStructure(ferrs, [...context, fld]);
                }
            }
        }
        return transformedErrors
    }

    return transformStructure(errs);
}

export const transformFormErrors = (err: any, form?: FormApi) => {
    if (isHttpError(err, '4xx')) return transformHttpErrors(err, form);
    if (!isHttpError(err)) captureException(err);
    return { [FORM_ERROR]: err }
}

export const formatErrorCode = (err: any) => isHttpError(err) ? ` (${err.response.status})` : '';

export const FormPageAlerts: React.SFC<{ topic?: string }> = ({ topic = 'form' }) => {
    const fs = useFormState({ subscription: { error: true, submitError: true } });
    let formErrors = [...errorsToList(fs.error), ...errorsToList(fs.submitError)];

    formErrors = formErrors.map(fe => {
        if (fe instanceof Error) {
            const errorCode = isHttpError(fe) ? ` (${fe.response.status})` : '';
            return `The ${topic} could not be saved${errorCode}.`
        } else {
            return fe;
        }
    })

    return <>{
        formErrors.map((err) => {
            return <DeclAlert variant="error" key={err}>
                <b>Error:</b> {err}
            </DeclAlert>
        })
    }</>
}

export const FieldCondition = ({ when, not = false, is, children }: { when: string, is: any | ((value) => boolean), children, not?: boolean }) => (
    <Field name={when} subscription={{ value: true }}>
        {({ input: { value } }) => (
            (typeof is == 'function' ? is(value) : value === is) != not ? children : null
        )}
    </Field>
);

const FieldPrefixContext = React.createContext<string>(null);

export const FieldPrefix = ({ prefix, children }) => (
    <FieldPrefixContext.Provider value={prefix}>
        {children}
    </FieldPrefixContext.Provider>
);

const resolveFieldPath = (...bits: string[]) => {
    let expBits = bits;
    expBits = expBits.filter(x => !!x);
    expBits = expBits.map(b => b.replace(/^\.\//, ''));
    expBits = expBits.filter(x => !!x);
    expBits = flatten<string>(expBits.map(b => b.split('/')));
    expBits = flatten<string>(expBits.map(b => b == '..' ? b : b.split('.')));

    let path = [];
    for (let b of expBits) {
        if (b == '$') {
            path = []
        } else if (b.startsWith('$')) {
            path = [b.slice(1)];
        } else if (b == '' || b == '..') {
            path.pop();
        } else {
            path.push(b)
        }
    }
    return path.join('.')
}

export const usePrefixed = (name: string) => {
    const prefix = useContext(FieldPrefixContext);
    return resolveFieldPath(prefix, name);
}

const FormAllSubscription: FormSubscription = formSubscriptionItems.reduce((result, key) => {
    result[key] = true
    return result
}, {})

const FieldAllSubscription: FieldSubscription = fieldSubscriptionItems.reduce((result, key) => {
    result[key] = true
    return result
}, {})

interface FSExtended {
    form: FormApi;
    changeRelative(path: string, value: any): void;
}

/**
 * This is an extension of FinalForm's useField.
 * It's primary purpose is to provide a MobX observable version of the FinalForm FieldState,
 * but it also adds a couple of properties (see FSExtended)
 */
export const useFieldState = <T extends any = any>(name: string): FieldState<T> & FSExtended => {
    const form: FormApi = useForm('useFieldState')
    const pfName = usePrefixed(name);

    const register = (callback: (state: FieldState<any>) => void) => form.registerField(pfName, callback, FieldAllSubscription, { silent: true });

    const nameRef = useRef(null);
    const obsRef = useRef(null);

    if (nameRef.current != pfName) {
        let initialValue: any;

        // temporarily disable destroyOnUnregister
        const destroyOnUnregister = form.destroyOnUnregister
        form.destroyOnUnregister = false

        register(state => {
            initialValue = state
        })()

        // return destroyOnUnregister to its original value
        form.destroyOnUnregister = destroyOnUnregister

        if (!obsRef.current) {
            obsRef.current = observable(initialValue, {}, { deep: false });
            Object.setPrototypeOf(obsRef.current, {
                get form() { return form },
                changeRelative(path, val) {
                    form.change(resolveFieldPath(nameRef.current, path), val);
                }
            } as FSExtended)
        } else {
            runInAction(() => {
                Object.assign(obsRef.current, initialValue)
            })
        }
        nameRef.current = pfName;
    }

    React.useEffect(() => {
        let firstRender = true;

        return register((state) => {
            if (firstRender) {
                firstRender = false;
                return;
            }

            // Fixes an issue where a state update may arrive after
            // this hook has been linked to a new field
            if (state.name != nameRef.current) return;

            runInAction(() => {
                Object.assign(obsRef.current, state);
            });
        })
    }, [pfName])

    return obsRef.current as any;
}

export const PrefixedField = <C extends FFAdapterComponent>({ name, ...props }: FieldComponentProps<C>) => (
    <Field name={usePrefixed(name)} {...props} />
);

export const usePrefixedField = (...args: Parameters<typeof useField>) => {
    const [name, ...pass] = args;
    const pfxd = usePrefixed(name);
    return useField(pfxd, ...pass);
}

export const FieldDecl: React.FC<FieldProps<any, any, any>> = ({ ...pass }) => {
    return <PrefixedField component={undefined as any} children={() => null} {...pass} />
}

export const WorkflowStateMask = () => {
    const statusState = usePrefixedField('workflow_state', { subscription: { value: true, initial: true } });
    if (statusState.input.value == 'archived') {
        return <LowMask />
    }
    return null;
}

export class OnFieldChange extends Component<{ name: string, children: (value: any, previous: any) => void }, { currentValue: any }> {
    constructor(props) {
        super(props);
        this.state = {
            currentValue: null,
        }
    }

    private lastValue: any;

    componentDidUpdate() {
        const { currentValue } = this.state;
        const lastValue = this.lastValue;
        if (currentValue !== lastValue) {
            this.lastValue = currentValue;
            this.props.children(currentValue, lastValue);
        }
    }

    onFieldRender = ({ input: { value } }: FieldRenderProps<any>) => {
        if (value !== this.state.currentValue) {
            this.setState({
                currentValue: value,
            })
        }
        return null;
    }

    render() {
        const { name } = this.props
        return <PrefixedField
            name={name}
            subscription={{ value: true }}
            allowNull
            render={this.onFieldRender}
        />
    }
}

export function useFormSubscription(
    callback: (state: FormState<any>, form: FormApi) => void,
    { subscription = FormAllSubscription, immediate }: { subscription?: FormSubscription, immediate?: boolean }
) {
    const form = useForm('useFormSubscription');
    const initialCall = useRef<boolean>(false);
    React.useEffect(() => form.subscribe((state) => {
        if (initialCall.current || immediate) callback(state, form)
        initialCall.current = true;
    }, subscription), [])
}

export const SubscribeForm: React.SFC<{
    callback: (state: FormState<any>, form: FormApi) => void,
    subscription?: FormSubscription
    immediate?: boolean,
}> = ({ callback, subscription, immediate }) => {
    useFormSubscription(callback, { subscription, immediate });
    return null;
}

export function useFieldSubscription(
    name: string,
    callback: (state: FieldState<any>, form: FormApi) => void,
    { subscription = FieldAllSubscription, immediate }: { subscription?: FieldSubscription, immediate?: boolean }
) {
    const form = useForm('useFieldSubscription');
    const pfName = usePrefixed(name);
    const initialCall = useRef<boolean>(false);
    React.useEffect(() => form.registerField(pfName, (state) => {
        if (initialCall.current || immediate) callback(state, form)
        initialCall.current = true;
    }, subscription), [pfName])
}

export const SubscribeField: React.SFC<{
    name: string,
    callback: (state: FieldState<any>, form: FormApi) => void,
    subscription?: FieldSubscription,
    immediate?: boolean,
}> = ({ name, callback, subscription, immediate }) => {
    useFieldSubscription(name, callback, { subscription, immediate });
    return null;
}
